import React from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from '../../lib/utils';

export interface DistributorShellTab<T extends string = string> {
  key: T;
  label: string;
  badge?: number;
  disabled?: boolean;
}

export interface DistributorShellStatus {
  label: string;
  tone: 'connected' | 'neutral' | 'warning' | 'error' | 'syncing';
  description?: string;
}

interface DistributorShellProps<T extends string = string> {
  lockup: React.ReactNode;
  title: string;
  currentCrumb: string;
  status: DistributorShellStatus;
  actions?: React.ReactNode;
  tabs: DistributorShellTab<T>[];
  activeTab: T;
  onTabChange: (tab: T) => void;
  banners?: React.ReactNode;
  children: React.ReactNode;
}

const statusClasses: Record<DistributorShellStatus['tone'], string> = {
  connected: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  neutral: 'bg-slate-100 text-slate-700 border-slate-200',
  warning: 'bg-amber-50 text-amber-700 border-amber-200',
  error: 'bg-rose-50 text-rose-700 border-rose-200',
  syncing: 'bg-blue-50 text-blue-700 border-blue-200',
};

const dotClasses: Record<DistributorShellStatus['tone'], string> = {
  connected: 'bg-emerald-500',
  neutral: 'bg-slate-400',
  warning: 'bg-amber-500',
  error: 'bg-rose-500',
  syncing: 'bg-blue-500 animate-pulse',
};

export function DistributorShell<T extends string>({
  lockup,
  title,
  currentCrumb,
  status,
  actions,
  tabs,
  activeTab,
  onTabChange,
  banners,
  children,
}: DistributorShellProps<T>) {
  return (
    <div data-testid="distributor-shell" className="h-full w-full flex-1 min-w-0 overflow-hidden flex flex-col bg-slate-50/40">
      <div data-testid="distributor-shell-header" className="shrink-0 w-full min-w-0 bg-white border-b border-slate-200">
        <div className="px-6 pt-4 pb-3">
          <div className="flex items-center gap-1.5 text-[12px] text-slate-500 mb-2.5">
            <span>Connectors</span>
            <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
            <span>Distributors</span>
            <ChevronRight className="h-3.5 w-3.5 text-slate-300" />
            <span className="text-slate-900 font-medium truncate">{currentCrumb}</span>
          </div>

          <div className="flex items-start justify-between gap-4 min-w-0">
            <div className="flex items-center gap-4 min-w-0">
              <div className="shrink-0">{lockup}</div>
              <div className="min-w-0">
                <h1 className="text-[18px] font-semibold text-slate-900 tracking-tight truncate">
                  {title}
                </h1>
                <div className="flex flex-wrap items-center gap-2 mt-0.5">
                  <span
                    className={cn(
                      'inline-flex items-center gap-1.5 rounded px-2 h-5 text-[11px] font-medium border',
                      statusClasses[status.tone],
                    )}
                  >
                    <span className={cn('w-1.5 h-1.5 rounded-full', dotClasses[status.tone])} />
                    {status.label}
                  </span>
                  {status.description && (
                    <span className="text-[11.5px] text-slate-500">
                      {status.description}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {actions && (
              <div className="flex flex-wrap items-center justify-end gap-2 shrink-0">
                {actions}
              </div>
            )}
          </div>
        </div>

        <div className="px-6 flex items-end min-w-0 overflow-x-auto">
          {tabs.map((tab) => {
            const active = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                type="button"
                onClick={() => !tab.disabled && onTabChange(tab.key)}
                disabled={tab.disabled}
                className={cn(
                  'relative h-10 px-4 text-[13px] font-medium transition-colors inline-flex items-center gap-2 whitespace-nowrap',
                  active ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900',
                  tab.disabled && 'opacity-40 cursor-not-allowed hover:text-slate-600',
                )}
              >
                {tab.label}
                {tab.badge !== undefined && tab.badge > 0 && (
                  <span
                    className={cn(
                      'inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded text-[10.5px] font-semibold tabular-nums',
                      tab.key === 'review'
                        ? 'bg-orange-100 text-orange-700'
                        : active
                          ? 'bg-slate-200 text-slate-700'
                          : 'bg-slate-100 text-slate-600',
                    )}
                  >
                    {tab.badge}
                  </span>
                )}
                {active && (
                  <span className="absolute inset-x-3 -bottom-px h-[2.5px] rounded-t-full bg-orange-500" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {banners && <div className="shrink-0 w-full px-6 pt-4 space-y-2">{banners}</div>}

      <div data-testid="distributor-shell-body" className="flex-1 min-h-0 min-w-0 w-full overflow-y-auto overflow-x-hidden">
        {children}
      </div>
    </div>
  );
}
